import React, { useContext, useEffect, useMemo, useRef } from "react";
import { Button, TextField, Typography, makeStyles } from "@material-ui/core";
import { RBModal } from "../../../components/RBModal";
import { RBButton } from "../../../components/RBButton";
import { RBContext } from "../../../components/RBContext";
import { FontSize } from "../../../theme/Theme";
import {
  RB_PAY_EMAIL_CODE_LENGTH,
  RB_PAY_SETUP_COLORS,
} from "../constants/rbPaySettings.constants";
import { buildRBPaySetupTheme } from "../utils/rbPaySettings.theme";

const C = RB_PAY_SETUP_COLORS;

const useStyles = makeStyles((theme) => ({
  content: {
    padding: 24,
    backgroundColor: C.pageBg,
    [theme.breakpoints.down("sm")]: {
      padding: 16,
    },
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 14,
    padding: 28,
    maxWidth: 460,
    width: "100%",
    margin: "0 auto",
    textAlign: "center",
    border: `1px solid ${C.cardBorder}`,
    [theme.breakpoints.down("sm")]: {
      padding: 20,
    },
  },
  infoBlock: {
    backgroundColor: C.summaryBg,
    border: `1px solid ${C.summaryBorder}`,
    borderRadius: 12,
    padding: 18,
    marginBottom: 22,
  },
  infoText: {
    fontSize: 14,
    color: C.textDark,
    lineHeight: 1.6,
  },
  infoSubText: {
    fontSize: 13,
    color: C.textMuted,
    lineHeight: 1.5,
    marginTop: 8,
  },
  errorText: {
    color: C.errorText,
    fontSize: FontSize.default,
    minHeight: 24,
    marginTop: 8,
  },
  successText: {
    color: C.successIcon,
    fontSize: FontSize.default,
    minHeight: 24,
    marginTop: 8,
  },
  resendButton: {
    padding: 0,
    minWidth: "auto",
    marginLeft: 2,
    textTransform: "uppercase",
    fontWeight: 700,
  },
  otpWrapper: {
    display: "flex",
    justifyContent: "center",
    gap: "8px",
    marginTop: 4,
    marginBottom: 18,
    flexWrap: "nowrap",
    [theme.breakpoints.down("sm")]: {
      gap: "2px",
    },
  },
  otpInput: {
    width: 52,
    [theme.breakpoints.down("sm")]: {
      width: 42,
    },
  },
  inputRoot: {
    "& input": {
      textAlign: "center",
      fontSize: FontSize.xlarge,
      fontWeight: "bold",
      padding: "12px 0",
    },
  },
  actionRow: {
    display: "flex",
    justifyContent: "center",
    marginTop: 12,
  },
  resendRow: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: 16,
    flexWrap: "wrap",
    gap: "2px",
  },
}));

interface RBPayEmailVerificationModalProps {
  open: boolean;
  email: string;
  code: string;
  errorMessage: string;
  successMessage: string;
  isRequesting: boolean;
  isVerifying: boolean;
  resendSecondsLeft: number;
  onClose: () => void;
  onCodeChange: (value: string) => void;
  onSubmit: () => void;
  onResend: () => void;
}

export const RBPayEmailVerificationModal: React.FC<RBPayEmailVerificationModalProps> = ({
  open,
  email,
  code,
  errorMessage,
  successMessage,
  isRequesting,
  isVerifying,
  resendSecondsLeft,
  onClose,
  onCodeChange,
  onSubmit,
  onResend,
}) => {
  const classes = useStyles();
  const { primaryColor, secondaryColor } = useContext(RBContext);
  const brandTheme = buildRBPaySetupTheme(primaryColor, secondaryColor);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const digits = useMemo(() => {
    const normalized = (code || "").slice(0, RB_PAY_EMAIL_CODE_LENGTH).split("");
    return Array.from({ length: RB_PAY_EMAIL_CODE_LENGTH }, (_, index) => normalized[index] || "");
  }, [code]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);
    }
  }, [open]);

  const updateDigit = (index: number, value: string) => {
    const nextDigits = [...digits];
    nextDigits[index] = value;
    onCodeChange(nextDigits.join(""));
  };

  const handleChange = (index: number, value: string) => {
    const nextCharacter = value.replace(/\D/g, "").slice(-1);
    updateDigit(index, nextCharacter);

    if (nextCharacter && index < RB_PAY_EMAIL_CODE_LENGTH - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
      return;
    }

    if (event.key === "Enter" && code.length === RB_PAY_EMAIL_CODE_LENGTH && !isVerifying) {
      onSubmit();
    }
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pastedDigits = event.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, RB_PAY_EMAIL_CODE_LENGTH);

    if (!pastedDigits) {
      return;
    }

    onCodeChange(pastedDigits);
    const targetIndex = Math.min(pastedDigits.length, RB_PAY_EMAIL_CODE_LENGTH) - 1;
    window.setTimeout(() => {
      inputRefs.current[Math.max(targetIndex, 0)]?.focus();
    }, 0);
  };

  const resendLabel = resendSecondsLeft > 0 ? `Resend in 00:${String(resendSecondsLeft).padStart(2, "0")}` : "Resend";

  return (
    <RBModal open={open} onClose={onClose} modalTitle="Email Verification" modalWidth={560}>
      <div className={classes.content}>
        <div className={classes.card}>
          <div className={classes.infoBlock}>
            <Typography className={classes.infoText}>
              For security, we need to verify this email address{" "}
              <strong style={{ color: brandTheme.secondary }}>{email}</strong> before it can be used to
              receive payments.
            </Typography>
            <Typography className={classes.infoSubText}>
              Please enter the <strong>6-digit verification code</strong> we just sent to this email.
            </Typography>
          </div>

          <div className={classes.otpWrapper}>
            {digits.map((digit, index) => (
              <TextField
                key={index}
                value={digit}
                variant="outlined"
                className={classes.otpInput}
                InputProps={{ classes: { root: classes.inputRoot } }}
                inputRef={(element) => {
                  inputRefs.current[index] = element;
                }}
                inputProps={{
                  maxLength: 1,
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  "aria-label": `Verification code digit ${index + 1}`,
                }}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(index, event)}
                onPaste={handlePaste}
              />
            ))}
          </div>

          {errorMessage ? <div className={classes.errorText}>{errorMessage}</div> : <div className={classes.errorText} />}
          {successMessage ? <div className={classes.successText}>{successMessage}</div> : <div className={classes.successText} />}

          <div className={classes.actionRow}>
            <RBButton
              text="Submit"
              buttonBackgroundColor={brandTheme.primary}
              onClick={onSubmit}
              isLoading={isVerifying}
              disabled={code.length !== RB_PAY_EMAIL_CODE_LENGTH || isRequesting}
            />
          </div>

          <div className={classes.resendRow}>
            <Typography variant="body2">Didn&apos;t receive a code?</Typography>
            <Button
              className={classes.resendButton}
              style={{ color: resendSecondsLeft > 0 ? C.textMuted : brandTheme.secondary }}
              onClick={onResend}
              disabled={isRequesting || isVerifying || resendSecondsLeft > 0}
            >
              {resendLabel}
            </Button>
          </div>
        </div>
      </div>
    </RBModal>
  );
};
